import { z } from 'zod'
import FormWrapper from 'app/frontend/base/components/FormWrapper'
import {
  usePageInfo,
  useUserTeamPreferences,
} from 'app/frontend/base/config/queries'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from '@tanstack/react-query'
import TeamPreferencesTable from './TeamPreferencesTable'
import { userTeamVisibilities } from 'app/frontend/types/custom/UserTeamVisibility'

const formSchema = z.object({
  stored_team_preferences_attributes: z.array(
    z.object({
      id: z.number().optional().or(z.null()),
      owner_type: z.string(),
      owner_id: z.number(),
      team_id: z.number().optional().or(z.null()),
      order_in_menu: z.number().optional().or(z.null()),
      visibility: z.enum(userTeamVisibilities),
      _destroy: z.boolean().optional(),
    }),
  ),
})

const UserSettingsTeamSection = () => {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const {
    data: { user },
  } = usePageInfo()

  // Render a row for each team the user is a member of, enrich it with any preference records that are already present
  const { data, isPending } = useUserTeamPreferences()

  if (!user || !data || isPending) return null

  return (
    <FormWrapper<
      wowaudit.User & {
        stored_team_preferences_attributes: wowaudit.UserTeamPreference[]
      }
    >
      formSchema={formSchema}
      entityType='user'
      entity={{
        ...user,
        stored_team_preferences_attributes: data.preferences ?? [],
      }}
      transformValues={(values) => {
        const globallyConfiguredGuilds =
          values.stored_team_preferences_attributes
            .filter(
              (pref: wowaudit.UserTeamPreference) => pref.team_id === null,
            )
            .map((pref: wowaudit.UserTeamPreference) => pref.owner_id)

        return {
          ...values,
          stored_team_preferences_attributes:
            values.stored_team_preferences_attributes.map(
              (pref: wowaudit.UserTeamPreference, i: number) => ({
                ...pref,
                order_in_menu: i,
                _destroy:
                  globallyConfiguredGuilds.includes(pref.owner_id) &&
                  pref.team_id !== null,
              }),
            ),
        }
      }}
      onSave={async (form, data) => {
        const { preferences } = (await queryClient.fetchQuery({
          queryKey: ['userTeamPreferences'],
        })) as { preferences: wowaudit.UserTeamPreference[] }

        await queryClient.invalidateQueries({ queryKey: ['pageInfo'] })

        form.reset({
          ...data,
          stored_team_preferences_attributes: preferences ?? [],
        })
      }}
    >
      <h4>{t('profile.team-preferences.title')}</h4>
      <p>{t('profile.team-preferences.introduction')}</p>

      <TeamPreferencesTable />
    </FormWrapper>
  )
}

export default UserSettingsTeamSection
